import { GooglePlacePrediction } from '@type/v1-api-types/googleMaps';
import {
  Button, ComboBox, RecordInterface, RecordOptionsInterface,
} from '@components/common/Forms';
import { useTranslation } from 'next-i18next';
import {
  MapIcon, MapPinIcon, TrashIcon, PlusCircleIcon,
} from '@heroicons/react/24/outline';
import useGoogle from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import { showJobAddressMapModal } from '@components/templates/job-add/edit/map/JobAddressMapModal';
import { dataTestId } from '@services/data-test-id';

/** Props */
interface JobAddressProps {
  record: RecordInterface,
  recordOptions?: RecordOptionsInterface,
  googleApiKey: string,
  inputName?: string,
  showAddMore?: boolean,
  showDelete?: boolean,
  currentAddress?: string,
  addAddress: () => void,
  deleteAddress: () => void,
  changeAddress?: (value: string) => void,
  saveAddresses?: () => void,
  currentIndex?: number,
}

/**
 * @description Job Address component. To be used in a form.
 * - `record` - record function from useBjForm
 * - `recordOptions` - record options for form
 * - `googleApiKey` - Google API key
 * - `inputName` - name of the input; default is 'addresses'
 * - `showAddMore` - show add more button; default is true
 * - `showDelete` - show delete button; default is false
 * - `currentAddress` - current address
 * - `addAddress` - add address function
 * - `deleteAddress` - delete address function
 * - `changeAddress` - change address function
 * - `saveAddresses` - save addresses function
 * - `currentIndex` - current index of the address
 */
export const JobAddress = (props: JobAddressProps) => {
  // Destructure props
  const {
    record,
    recordOptions,
    googleApiKey,
    inputName = 'addresses',
    showAddMore = true,
    showDelete = false,
    currentAddress,
    addAddress,
    deleteAddress,
    changeAddress,
    saveAddresses,
    currentIndex,
  } = props;

  // Translation
  const { t, i18n } = useTranslation();

  // Google location hook
  const {
    placePredictions,
    getPlacePredictions,
  } = useGoogle({
    apiKey: googleApiKey,
    debounce: 300,
    language: i18n.language,
  });

  // Render
  // **************************
  return (
    <div>
      {/* Job address */}
      <div className="relative mt-1 flex items-start gap-2">
        <MapPinIcon
          className="absolute left-3 top-6 z-1 size-5 -translate-y-1/2 text-ink"
        />
        <ComboBox
          required
          placeholder={t('job.add.label.job_location_address_placeholder')}
          className="grow text-ellipsis pl-10 pr-18"
          options={{
            valueFromKey: 'description',
            staticList: placePredictions,
          }}
          onInputValueChange={(inputValue) => {
            if (inputValue && inputValue.length > 2) getPlacePredictions({ input: inputValue });
          }}
          onBlur={saveAddresses}
          renderList={(
            item,
            index,
            listKey,
            getItemProps,
            listStyling,
            highlightedIndex,
          ) => {
            const place = item as GooglePlacePrediction;
            const highlightClass = highlightedIndex === index ? 'text-white' : 'text-ink-medium';
            return (
              <li key={listKey} className={`${listStyling} flex-col`} {...getItemProps({ item, index })}>
                <span>{place?.structured_formatting?.main_text}</span>
                <span className={`${highlightClass} -mt-0.5 text-tiny font-normal transition-all duration-150`}>
                  {place?.structured_formatting?.secondary_text}
                </span>
              </li>
            );
          }}
          {...record(inputName, recordOptions)}
        />

        {/* Delete button */}
        {showDelete && (
          <Button
            color="light"
            styling="outline"
            size="xs"
            className="shrink-0 !px-1 !py-4"
            title={t('global.action.remove')}
            onClick={deleteAddress}
            data-test-id={`jobAdd-deleteButton_${currentIndex}`}
          >
            <TrashIcon className="size-4 text-error/80" />
          </Button>
        )}
      </div>

      <div className={`mt-3 flex flex-row-reverse justify-between gap-2 ${showDelete ? 'md:ml-3.5 md:mr-12' : 'md:mx-3.5'}`}>
        {/* Open map */}
        <Button
          color="light"
          styling="soft"
          size="sm"
          rounding="full"
          className="gap-2 !py-1.5 !pl-3.5 !pr-4 !font-normal leading-tight"
          onClick={() => showJobAddressMapModal(googleApiKey, currentAddress, changeAddress)}
          data-test-id={`jobAdd-openMapButton_${currentIndex}`}
        >
          <MapIcon className="size-4" />
          {t('add.job.open.map')}
        </Button>

        {/* Add another location */}
        {showAddMore && (
          <Button
            color="light"
            styling="soft"
            size="sm"
            rounding="full"
            className="gap-1 !py-1.5 !pl-3 !pr-4 !font-normal leading-tight"
            onClick={addAddress}
            {...dataTestId('jobAddPage.jobAddress.addButton')}
          >
            <PlusCircleIcon className="size-4" />
            {t('job.add.collection_add.location')}
          </Button>
        )}
      </div>
    </div>
  );
};
